import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDateOnly } from '@dosfilos/shared';
import { useTenantDateFormat } from '../../../stores/tenant.store';
import type {
  RegenProtectReason,
  RegenSlot,
  SyllabusRegenerationPreviewDto,
} from '../../../lib/api/course-template.api';

// ─── Volver a aplicar la plantilla: qué pasa con las sesiones (S5) ──────────
// Antes de confirmar: cuántas se reemplazan, se agregan y se quitan; cada
// sesión que se MUEVE con su fecha y hora actual → nueva (en las secciones
// antiguas toda sesión tiene la fecha «elegida», así que un ajuste hecho a mano
// no se distingue y hay que verlo); y las protegidas, con su motivo, que solo
// se tocan marcando la casilla. Un día con asistencia (opción B): lo dictado
// queda, sus textos clave siguen a la plantilla, y sus sobrantes se listan uno
// por uno, aparte, porque salen solo con la casilla (o nunca, si tienen candado).

/** S6·2 (decisión S6-c): lo que exige marcar «Generar igual» — sesiones
 *  recortadas a menos de la mitad de lo que piden y sesiones que no caben. El
 *  servidor aplica la misma regla y rechaza sin la casilla. */
export function fitNeedsConfirmation(preview: SyllabusRegenerationPreviewDto | null): {
  truncated: number;
  unplaced: number;
} {
  const fit = preview?.fit ?? { truncated: [], unplaced: [] };
  return {
    truncated: fit.truncated.filter((x) => x.belowMinimum).length,
    unplaced: fit.unplaced.length,
  };
}

export function RegenerationPreview({
  preview,
  loading,
  confirmProtected,
  onConfirmProtected,
  confirmFit,
  onConfirmFit,
}: {
  preview: SyllabusRegenerationPreviewDto | null;
  loading: boolean;
  confirmProtected: boolean;
  onConfirmProtected: (v: boolean) => void;
  /** S6·2: «Generar igual con N sesiones recortadas y M que no caben». */
  confirmFit: boolean;
  onConfirmFit: (v: boolean) => void;
}): JSX.Element | null {
  const { t } = useTranslation('sis');
  const fmt = useTenantDateFormat();
  const k = 'templateEditor.regen';
  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={14} />
        <Typography variant="caption">{t(`${k}.loading`)}</Typography>
      </Stack>
    );
  }
  if (!preview) return null;
  const s = preview.summary;
  // S6: lo que la jornada no deja entero (puede venir de un servidor anterior).
  const fit = preview.fit ?? { truncated: [], unplaced: [] };
  const needs = fitNeedsConfirmation(preview);
  const slot = (x: RegenSlot) =>
    t(`${k}.slot`, { date: formatDateOnly(x.date, fmt), start: x.startTime, end: x.endTime });
  const reasons = (r: RegenProtectReason[]) =>
    r.map((x) => (
      <Chip key={x} size="small" color="warning" variant="outlined" label={t(`${k}.reason.${x}`)} />
    ));
  const nothing =
    s.add === 0 &&
    s.remove === 0 &&
    s.skippedOnAttendanceDate === 0 &&
    preview.attendanceDayRemovals.length === 0 &&
    preview.changes.length === 0;
  const needsConfirm = s.protectedByEdit > 0 || s.removeOnAttendanceDay > 0;

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {t(`${k}.title`)}
      </Typography>
      {(fit.truncated.length > 0 || fit.unplaced.length > 0) && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {t(`${k}.fit.summary`, {
              truncated: fit.truncated.length,
              unplaced: fit.unplaced.length,
            })}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
            {t(`${k}.fit.hint`)}
          </Typography>
          <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
            {fit.truncated.map((x, i) => (
              <Stack
                key={`t-${i}`}
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ py: 0.25 }}
              >
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {slot(x.slot)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {x.slot.topic}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={x.belowMinimum ? 'error' : 'warning'}
                  label={t('templateEditor.modules.fit.chip', {
                    actual: x.actualMinutes,
                    requested: x.requestedMinutes,
                  })}
                />
              </Stack>
            ))}
            {fit.unplaced.map((x, i) => (
              <Stack
                key={`u-${i}`}
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ py: 0.25 }}
              >
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatDateOnly(x.date, fmt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {x.topic}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color="error"
                  label={t(`${k}.fit.unplacedChip`)}
                />
              </Stack>
            ))}
          </Box>
          {(needs.truncated > 0 || needs.unplaced > 0) && (
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Checkbox
                  size="small"
                  checked={confirmFit}
                  onChange={(e) => onConfirmFit(e.target.checked)}
                />
              }
              label={t(`${k}.fit.confirm`, {
                truncated: t(`${k}.fit.confirmTruncated`, { count: needs.truncated }),
                unplaced: t(`${k}.fit.confirmUnplaced`, { count: needs.unplaced }),
              })}
            />
          )}
        </Alert>
      )}
      {nothing ? (
        <Typography variant="body2" color="text.secondary">
          {t(`${k}.noChanges`, { count: s.replace })}
        </Typography>
      ) : (
        <Stack spacing={1}>
          <Typography variant="body2" fontWeight={600}>
            {t(`${k}.summary`, { replaced: s.replace, added: s.add, removed: s.remove })}
          </Typography>
          {s.keyTextsCleared > 0 && (
            <Typography variant="body2">
              {t(`${k}.keyTextsCleared`, { count: s.keyTextsCleared })}
            </Typography>
          )}
          {s.manual > 0 && (
            <Typography variant="body2" color="text.secondary">
              {t(`${k}.manual`, { count: s.manual })}
            </Typography>
          )}

          {preview.changes.length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={700}>
                {t(`${k}.changesTitle`, { count: preview.changes.length })}
              </Typography>
              <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                {preview.changes.map((c, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {c.moved
                        ? t(`${k}.moved`, { from: slot(c.from), to: slot(c.to) })
                        : slot(c.to)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {c.topicChanged
                        ? t(`${k}.topicChanged`, { from: c.from.topic, to: c.to.topic })
                        : c.to.topic}
                    </Typography>
                    {c.keyTextsCleared ? (
                      <Chip size="small" variant="outlined" label={t(`${k}.keyTextsChip`)} />
                    ) : (
                      c.keyTextsChanged && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t(`${k}.keyTextsChangedChip`)}
                        />
                      )
                    )}
                    {reasons(c.protect)}
                  </Stack>
                ))}
              </Box>
            </Box>
          )}

          {preview.removals.length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={700}>
                {t(`${k}.removalsTitle`, { count: preview.removals.length })}
              </Typography>
              {preview.removals.map((r, i) => (
                <Stack
                  key={i}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ py: 0.5 }}
                >
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {slot(r.slot)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {r.slot.topic}
                  </Typography>
                  {reasons(r.protect)}
                </Stack>
              ))}
            </Box>
          )}

          {preview.attendanceDayRemovals.length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={700}>
                {t(`${k}.attendanceRemovalsTitle`, {
                  count: preview.attendanceDayRemovals.length,
                })}
              </Typography>
              {preview.attendanceDayRemovals.map((r, i) => (
                <Stack
                  key={i}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ py: 0.5 }}
                >
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {slot(r.slot)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {r.slot.topic}
                  </Typography>
                  {r.locked ? (
                    reasons(r.protect.filter((x) => x !== 'attendance'))
                  ) : (
                    <Chip size="small" variant="outlined" label={t(`${k}.attendanceRemovalChip`)} />
                  )}
                </Stack>
              ))}
            </Box>
          )}

          {preview.skippedOnAttendanceDate.length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={700}>
                {t(`${k}.skippedTitle`, { count: preview.skippedOnAttendanceDate.length })}
              </Typography>
              {preview.skippedOnAttendanceDate.map((x, i) => (
                <Stack
                  key={i}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ py: 0.5 }}
                >
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {slot(x)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {x.topic}
                  </Typography>
                </Stack>
              ))}
            </Box>
          )}

          {s.protectedByAttendance > 0 && (
            <Alert severity="info">
              {t(`${k}.protectedAttendance`, { count: s.protectedByAttendance })}
            </Alert>
          )}
          {needsConfirm && (
            <Alert severity="warning">
              {s.protectedByEdit > 0 && (
                <Typography variant="body2">
                  {t(`${k}.protectedEdit`, { count: s.protectedByEdit })}
                </Typography>
              )}
              {s.removeOnAttendanceDay > 0 && (
                <Typography variant="body2">
                  {t(`${k}.attendanceRemovalsConfirm`, { count: s.removeOnAttendanceDay })}
                </Typography>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={confirmProtected}
                    onChange={(e) => onConfirmProtected(e.target.checked)}
                  />
                }
                label={t(`${k}.confirmProtected`)}
              />
            </Alert>
          )}
        </Stack>
      )}
    </Box>
  );
}
